import axios from "axios";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
  createAction,
} from "@reduxjs/toolkit";
import { RootState, AppDispatch } from "../../redux/storeTypes";

import { logger } from "../../utils/logging/logger";
import {
  enterChatApi,
  sendMessageApi,
  editMessageApi,
  fetchMessagesApi,
  searchMessagesApi,
  fetchMessageByIdApi,
  getDialogueKeysApi,
  normalizeDialogueKeys,
  markMessagesAsReadApi,
  getSeenByUsersApi,
  markMessageAsDeliveredApi,
  createNewDialogueApi,
  uploadFileApi,
  accessMediaApi,
  softDeleteMessageApi,
  hardDeleteMessageApi,
  fetchLastSeenApi,
  fetchUnreadCountsApi,
  createGroupApi,
  updateGroupImageApi,
  updateGroupInfoApi,
  smartDeleteDialogueApi,
  addParticipantApi,
  removeParticipantApi,
  fetchDialogueBySlugApi,
  fetchGroupParticipantsApi,
  promoteToElderApi,
  demoteToParticipantApi,
  resignElderRoleApi,
  leaveGroupApi,
  transferFounderApi,
  markDialogueApi,
  unmarkDialogueApi,
} from "../../services/conversation/conversationApi";
import { setUserOnlineStatus } from "../presence/presenceSlice";
import {
  cacheDecryptedMedia,
  getDecryptedMediaFromCache,
} from "../../lib/dexie/mediaCacheUtils";

import {
  decryptMessageLocally,
  encryptMessageLocallyForAllDevices,
  decryptAESKeyWithPrivateKey,
  encryptAESKeyWithPublicKey,
  decryptFileWithAES,
  encryptFileWithAES,
  base64ToUint8Array,
} from "../../utils/encryptionUtils";

import {
  decryptMessageLocallyEx,
  type DecryptErr,
} from "../../utils/encryptionUtils";

import {
  ConversationState,
  MediaErrCode,
  AccessMediaArgs,
} from "../../types/conversation/conversationTypes";
import {
  getMimeTypeFromMediaType,
  normalizeUploadFile,
  detectMimeFromBytes,
} from "../../utils/mediaTools/mimeUtils";
import {
  getPrivateKey,
  getMyPublicKey,
  getDeviceIdAsync,
} from "../../utils/keyManager";
import {
  Dialogue,
  Message,
  UserDialogueMarker,
  Participant,
  SeenUser,
} from "../../types/conversation/conversationTypes";
import websocketManager from "../../lib/websocketManager";
import { isBase64 } from "../../utils/encryptionUtils";
import { resolvePlayback } from "@/asset-delivery/resolver";
import type {
  PlaybackTarget,
  PlaybackKind,
} from "@/asset-delivery/orchestrator/types";

// Helpers ----------------------------------------------------------------------------------
const toPlaybackKind = (
  mediaType: "image" | "video" | "audio" | "file",
): PlaybackKind => {
  if (mediaType === "image") return "image";
  if (mediaType === "video") return "video";
  if (mediaType === "audio") return "audio";
  return "file";
};

const messagePlaybackTarget = (messageId: number): PlaybackTarget => ({
  app_label: "conversation",
  model: "message",
  object_id: messageId,
});

// Initial State -----------------------------------------------------------------------------
const initialState: ConversationState = {
  dialogues: [],
  loading: false,
  error: null,
  uploadControllers: {},
  typingStatus: {},
  recordingStatus: {},
  fileUploadStatus: {},
  lastSeenMap: {},
  groupParticipants: {},
  searchResults: {},
  searchLoading: false,
  searchError: null,
  scrollToMessageId: null,
  highlightedMessageId: null,
  unreadCounts: {},
  userDialogueMarkers: [],

  seenByUsers: {}, // { [messageId: number]: SeenUser[] }
  seenByLoading: false,
  seenByError: null,
  mediaBlobs: {},

  accessGranted: false,
  accessExpiresAt: null,
  remainingSeconds: null,

  mediaHydrationDone: false,
};

const uploadControllersMap: Map<string, AbortController> = new Map();

// ---------------------------------------------------------------------------------------------
export const clearConversationCache = createAction("conversation/clearCache");

// helper: abort & clear all in-flight uploads living outside redux state
function abortAndClearAllUploads() {
  try {
    uploadControllersMap.forEach((controller) => {
      try {
        controller.abort();
      } catch {}
    });
  } finally {
    uploadControllersMap.clear();
  }
}

// Helper --------------------------------------------------------------------------------------
type DecryptMeta = {
  decrypted_content: string | null;
  decrypt_error_code?: DecryptErr["code"];
};

// centralized logic for a single message
async function resolveEncryptedContentForMessage(
  msg: any,
  privateKey: CryptoKey,
  deviceId: string,
): Promise<DecryptMeta> {
  // files are handled elsewhere
  if (msg.is_encrypted_file) return { decrypted_content: null };

  // plain/legacy
  if (!msg.is_encrypted) {
    const text = msg.content ?? msg.content_encrypted ?? "";
    return { decrypted_content: text };
  }

  // missing payload
  if (!msg.content_encrypted) {
    return { decrypted_content: null, decrypt_error_code: "PAYLOAD" };
  }

  // device targeting (quick reject)
  if (msg.encrypted_for_device && msg.encrypted_for_device !== deviceId) {
    return { decrypted_content: null, decrypt_error_code: "WRONG_DEVICE" };
  }

  // try local decrypt
  const res = await decryptMessageLocallyEx(msg.content_encrypted, privateKey);
  if (res.ok) return { decrypted_content: res.text };
  return { decrypted_content: null, decrypt_error_code: res.code };
}

// Get System Messages --------------------------------------------------------------------------
export const selectSystemMessages = (
  state: RootState,
  dialogueSlug: string,
): Message[] => {
  const dialogue = state.conversation.dialogues.find(
    (d) => d.slug === dialogueSlug,
  );
  if (!dialogue || !dialogue.messages) return [];
  return dialogue.messages.filter((m) => m.is_system === true);
};

// Create New Dialogue ---------------------------------------------------------------------------
export const createNewDialogue = createAsyncThunk<
  { dialogue?: Dialogue; message: string },
  { recipient_id: number; check_only?: boolean },
  { rejectValue: any }
>(
  "conversation/createNewDialogue",
  async ({ recipient_id, check_only = false }, { rejectWithValue }) => {
    try {
      const response = await createNewDialogueApi(recipient_id, check_only);
      return response;
    } catch (error: any) {
      if (error.code === 204) {
        return rejectWithValue({
          message: error.message,
          code: 204,
        });
      }
      return rejectWithValue(
        error.response?.data || { message: "Unknown error" },
      );
    }
  },
);

// Mark Dialogue ---------------------------------------------------------------------------------
export const markDialogueAsSensitive = createAsyncThunk<
  UserDialogueMarker,
  { dialogue_slug: string; is_sensitive?: boolean; delete_policy?: string },
  { rejectValue: string }
>("conversation/markDialogueAsSensitive", async (data, thunkAPI) => {
  try {
    const result = await markDialogueApi(data);

    thunkAPI.dispatch(
      updateDialogueMarker({
        dialogueSlug: result.dialogue_slug, // backend باید این را بازگرداند
        is_sensitive: result.is_sensitive,
        marker_id: result.id,
      }),
    );

    return result;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to mark dialogue as sensitive");
  }
});
// Unmark Dialogue ------------------------------------------------------------------------------
export const unmarkDialogueAsSensitive = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("conversation/unmarkDialogueAsSensitive", async (markerId, thunkAPI) => {
  try {
    await unmarkDialogueApi(markerId);

    const state = thunkAPI.getState();
    const marker = state.conversation.userDialogueMarkers.find(
      (m) => m.id === markerId,
    );
    if (marker) {
      thunkAPI.dispatch(
        updateDialogueMarker({
          dialogueSlug: marker.dialogue_slug,
          is_sensitive: false,
          marker_id: null,
        }),
      );
    }

    return markerId;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to unmark dialogue");
  }
});

// Enter chat with security PIN ---------------------------------------------------
export const enterChat = createAsyncThunk<
  { dialogues: Dialogue[] },
  void, // چون دیگر PIN نمی‌فرستیم
  { rejectValue: string }
>("conversation/enterChat", async (_, { rejectWithValue }) => {
  try {
    const response = await enterChatApi();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to fetch dialogues",
    );
  }
});

// Last Seen -----------------------------------------------------------------------------
export const fetchLastSeen = createAsyncThunk<
  { dialogueSlug: string; lastSeenData: any },
  string,
  { dispatch: AppDispatch }
>(
  "conversation/fetchLastSeen",
  async (dialogueSlug, { dispatch, rejectWithValue }) => {
    try {
      const lastSeenData = await fetchLastSeenApi(dialogueSlug);

      // Sync presence map with REST result
      dispatch(
        setUserOnlineStatus({
          userId: lastSeenData.user_id,
          isOnline: !!lastSeenData.is_online,
        }),
      );

      return { dialogueSlug, lastSeenData };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch last seen",
      );
    }
  },
);

// Fetch Unread Counts --------------------------------------------------------------------
export const fetchUnreadCounts = createAsyncThunk(
  "conversation/fetchUnreadCounts",
  async (_, thunkAPI) => {
    try {
      return await fetchUnreadCountsApi();
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to fetch unread counts");
    }
  },
);

// Send a message ---------------------------------------------------------------------------
export const sendMessage = createAsyncThunk(
  "conversation/sendMessage",
  async (
    data: { dialogue_slug: string; content: string },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const user = state.auth.user;
      const deviceId = await getDeviceIdAsync();
      if (!deviceId) throw new Error("Device ID not found");

      const dialogue = state.conversation.dialogues.find(
        (d) => d.slug === data.dialogue_slug,
      );
      if (!dialogue) throw new Error("Dialogue not found");

      const isEncrypted = !dialogue.is_group;
      let encryptedContents: {
        device_id: string;
        encrypted_content: string;
      }[] = [];

      if (isEncrypted) {
        const resp = await getDialogueKeysApi(data.dialogue_slug, {
          full: true,
        });
        const { verified, unverified } = normalizeDialogueKeys(resp);

        let partnerKeys: Array<{ device_id: string; public_key: string }> =
          verified;
        if (!partnerKeys.length && unverified.length) {
          partnerKeys = unverified;
        }
        if (!partnerKeys.length) throw new Error("No public keys found");

        const myPublicKey = await getMyPublicKey();
        if (!myPublicKey) throw new Error("Your public key not found");

        const allKeysRaw = [
          ...partnerKeys,
          { device_id: deviceId, public_key: myPublicKey },
        ];

        const allKeys = Array.from(
          new Map(
            allKeysRaw
              .filter((k) => k?.device_id && k?.public_key)
              .map((k) => [String(k.device_id).trim().toLowerCase(), k]),
          ).values(),
        ).filter(
          (k) =>
            typeof k.public_key === "string" &&
            k.public_key.includes("-----BEGIN PUBLIC KEY-----") &&
            k.public_key.includes("-----END PUBLIC KEY-----"),
        );

        const out = await encryptMessageLocallyForAllDevices(
          data.content,
          allKeys,
        );
        encryptedContents = out.filter(
          (e) =>
            e &&
            e.encrypted_content &&
            e.encrypted_content !== "[Encryption failed]",
        );
        if (!encryptedContents.length) {
          throw new Error("Encryption failed for all devices");
        }
      }

      let tempId: string | null = null;
      if (websocketManager.isConnected()) {
        tempId = `ws-${Date.now()}`;

        websocketManager.sendMessage("conversation", {
          type: "chat_message",
          dialogue_slug: data.dialogue_slug,
          is_encrypted: isEncrypted,
          ...(isEncrypted
            ? { encrypted_contents: encryptedContents }
            : { content: data.content }),
        });

        return {
          dialogue_slug: data.dialogue_slug,
          content: data.content,
          is_encrypted: isEncrypted,
          encrypted_contents: encryptedContents,
          user,
          tempId,
        };
      }

      // ---------------------------------------
      const payload = isEncrypted
        ? {
            dialogue_slug: data.dialogue_slug,
            is_encrypted: true,
            encrypted_contents: encryptedContents,
          }
        : {
            dialogue_slug: data.dialogue_slug,
            is_encrypted: false,
            encrypted_contents: [
              { device_id: "plain", encrypted_content: data.content },
            ],
            content: data.content,
          };

      const response = await sendMessageApi(payload as any);

      return {
        dialogue_slug: response.dialogue_slug ?? data.dialogue_slug,
        message_id: response.message_id,
        content: data.content,
        is_encrypted: isEncrypted,
        encrypted_contents: encryptedContents,
        user,
        tempId,
      };
    } catch (error: any) {
      return rejectWithValue({
        error:
          error?.response?.data || error?.message || "Failed to send message",
        tempId: `api-${Date.now()}`,
      });
    }
  },
);

// Edit Message (WebSocket + API fallback) ----------------------------------------------------
export const editMessage = createAsyncThunk(
  "conversation/editMessage",
  async (
    data: { message_id: number; dialogue_slug: string; new_content: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const dialogue = state.conversation.dialogues.find(
        (d) => d.slug === data.dialogue_slug,
      );
      if (!dialogue) throw new Error("Dialogue not found");

      const isEncrypted = !dialogue.is_group;
      const deviceId = await getDeviceIdAsync();

      let encryptedContents: {
        device_id: string;
        encrypted_content: string;
      }[] = [];

      if (isEncrypted) {
        // 1)
        const resp = await getDialogueKeysApi(data.dialogue_slug, {
          full: true,
        });
        const { verified, unverified } = normalizeDialogueKeys(resp);

        // 2)
        let partnerKeys: Array<{ device_id: string; public_key: string }> =
          verified;
        if (!partnerKeys.length && unverified.length) {
          partnerKeys = unverified;
        }
        if (!partnerKeys.length) throw new Error("No public keys found");

        // 3)
        const myPublicKey = await getMyPublicKey();
        if (!deviceId || !myPublicKey) {
          throw new Error("Device ID or Public Key not found");
        }

        const allKeysRaw = [
          ...partnerKeys,
          { device_id: deviceId, public_key: myPublicKey },
        ];

        // 4)
        const allKeys = Array.from(
          new Map(
            allKeysRaw
              .filter((k) => k?.device_id && k?.public_key)
              .map((k) => [String(k.device_id).trim().toLowerCase(), k]),
          ).values(),
        ).filter(
          (k) =>
            typeof k.public_key === "string" &&
            k.public_key.includes("-----BEGIN PUBLIC KEY-----") &&
            k.public_key.includes("-----END PUBLIC KEY-----"),
        );

        // 5)
        const out = await encryptMessageLocallyForAllDevices(
          data.new_content,
          allKeys,
        );

        // 6)
        encryptedContents = out.filter(
          (e) =>
            e &&
            e.encrypted_content &&
            e.encrypted_content !== "[Encryption failed]",
        );
        if (!encryptedContents.length) {
          throw new Error("Failed to encrypt content for any device");
        }
      }

      // 7)
      if (websocketManager.isConnected()) {
        websocketManager.sendMessage("conversation", {
          type: "edit_message",
          message_id: data.message_id,
          dialogue_slug: data.dialogue_slug,
          is_encrypted: isEncrypted,
          ...(isEncrypted
            ? { encrypted_contents: encryptedContents }
            : { content: data.new_content }),
        });
      }

      // 8) API fallback
      const payload = isEncrypted
        ? {
            message_id: data.message_id,
            dialogue_slug: data.dialogue_slug,
            is_encrypted: true,
            encrypted_contents: encryptedContents,
          }
        : {
            message_id: data.message_id,
            dialogue_slug: data.dialogue_slug,
            is_encrypted: false,
            content: data.new_content,
          };

      const response = await editMessageApi(payload as any);
      return {
        message_id: data.message_id,
        dialogue_slug: data.dialogue_slug,
        new_content: data.new_content,
        edited_at: new Date().toISOString(),
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to edit message",
      );
    }
  },
);

// Soft Delete Message (WebSocket + API fallback) ----------------------------------------------------
export const softDeleteMessage = createAsyncThunk(
  "conversation/softDeleteMessage",
  async (
    data: { message_id: number; dialogue_slug: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { message_id, dialogue_slug } = data;
      const state = getState() as RootState;

      if (websocketManager.isConnected()) {
        websocketManager.sendMessage("conversation", {
          type: "soft_delete_message",
          dialogue_slug,
          message_id,
        });

        return {
          dialogue_slug,
          message_id,
          user_id: state.auth.user?.id,
        };
      }

      await softDeleteMessageApi(message_id);

      return {
        dialogue_slug,
        message_id,
        user_id: state.auth.user?.id,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Soft delete failed");
    }
  },
);

// Hard Delete Message (WebSocket + API fallback) ----------------------------------------------------
export const hardDeleteMessage = createAsyncThunk(
  "conversation/hardDeleteMessage",
  async (
    data: { message_id: number; dialogue_slug: string },
    { getState, rejectWithValue },
  ) => {
    const { message_id, dialogue_slug } = data;
    const state = getState() as RootState;

    try {
      if (websocketManager.isConnected()) {
        websocketManager.sendMessage("conversation", {
          type: "hard_delete_message",
          dialogue_slug,
          message_id,
        });

        return { viaSocket: true };
      }

      await hardDeleteMessageApi(message_id);

      return {
        dialogue_slug,
        message_id,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Hard delete failed");
    }
  },
);

// Upload File Thunk ----------------------------------------------------------------------
export const uploadFile = createAsyncThunk(
  "conversation/uploadFile",
  async (
    data: {
      dialogue_slug: string;
      file: File;
      file_type: "image" | "video" | "audio" | "file";
      dialogue: Dialogue;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const state = getState() as RootState;
      const user = state.auth.user;
      const { dialogue_slug, file, file_type, dialogue } = data;

      // UI status
      dispatch(
        setFileUploadStatus({
          dialogueSlug: dialogue_slug,
          fileType: file_type,
          status: "uploading",
          progress: 0,
        }),
      );

      const controller = new AbortController();
      uploadControllersMap.set(dialogue_slug, controller);

      const isEncrypted = !dialogue.is_group;

      // Smart MIME/filename normalization (no transcoding)
      let safeFile = normalizeUploadFile(file, file_type);

      // Outputs from crypto/upload pipeline
      let encryptedFileBlob: Blob = safeFile;
      let aesKey: Uint8Array | null = null;
      let nonce: Uint8Array | null = null;
      let encrypted_aes_key_base64: string | null = null; // convenience for current device
      const encryptedKeysPerDevice: { [deviceId: string]: string } = {};

      if (isEncrypted) {
        // 1) Fetch partner keys (verified/unverified)
        const resp = await getDialogueKeysApi(dialogue_slug, { full: true });
        const { verified, unverified } = normalizeDialogueKeys(resp);

        // 2) Prefer verified; fallback to unverified
        let partnerKeys: Array<{ device_id: string; public_key: string }> =
          verified;
        if (!partnerKeys.length && unverified.length) {
          partnerKeys = unverified;
          // TODO: optional UX hint
        }
        if (!partnerKeys.length) {
          throw new Error("No public keys found");
        }

        // 3) Add my own key so the file is readable immediately on this device
        const myPublicKey = await getMyPublicKey();
        const deviceId = await getDeviceIdAsync(); // ✅ canonical current device_id (fingerprint)
        if (!deviceId || !myPublicKey)
          throw new Error("Device ID or Public Key not found");

        const allKeysRaw = [
          ...partnerKeys,
          { device_id: deviceId, public_key: myPublicKey },
        ];

        // 4) Dedupe by device_id + validate PEMs
        const allKeys = Array.from(
          new Map(
            allKeysRaw
              .filter((k) => k?.device_id && k?.public_key)
              .map((k) => [String(k.device_id).trim().toLowerCase(), k]),
          ).values(),
        ).filter(
          (k) =>
            typeof k.public_key === "string" &&
            k.public_key.includes("-----BEGIN PUBLIC KEY-----") &&
            k.public_key.includes("-----END PUBLIC KEY-----"),
        );

        // 5) Encrypt file with AES-GCM (Hybrid encryption)
        const result = await encryptFileWithAES(safeFile);
        encryptedFileBlob = result.encryptedFileBlob;
        aesKey = result.aesKey;
        nonce = result.nonce;

        // 6) RSA-OAEP(wrapped AES key + nonce) per device
        for (const { device_id, public_key } of allKeys) {
          const enc = await encryptAESKeyWithPublicKey(
            aesKey,
            nonce,
            public_key,
          );
          encryptedKeysPerDevice[device_id] = enc; // base64
        }

        // 7) Convenience: also include current device’s wrapped key separately
        encrypted_aes_key_base64 = encryptedKeysPerDevice[deviceId];
      }

      // Choose MIME type
      const mimeType =
        safeFile.type || getMimeTypeFromMediaType(file_type, safeFile.name);

      // 8) Upload via API (no plaintext keys/nonce)
      const response = await uploadFileApi(
        {
          dialogue_slug,
          file: isEncrypted
            ? new File([encryptedFileBlob], `encrypted_${safeFile.name}`, {
                type: mimeType,
              })
            : safeFile,
          file_type,
          is_encrypted_file: isEncrypted,
          aes_key_encrypted: encrypted_aes_key_base64 ?? undefined,
          encrypted_keys_per_device: isEncrypted
            ? encryptedKeysPerDevice
            : undefined,
        },
        (percent) => {
          dispatch(
            setFileUploadStatus({
              dialogueSlug: dialogue_slug,
              fileType: file_type,
              status: "uploading",
              progress: percent,
            }),
          );

          websocketManager.sendMessage("conversation", {
            type: "file_upload_status",
            dialogue_slug,
            file_type,
            status: "uploading",
            progress: percent,
          });
        },
        controller,
      );

      // 9) Update status after upload
      dispatch(
        setFileUploadStatus({
          dialogueSlug: dialogue_slug,
          fileType: file_type,
          status: "processing",
        }),
      );
      websocketManager.sendMessage("conversation", {
        type: "file_upload_status",
        dialogue_slug,
        file_type,
        status: "processing",
      });

      setTimeout(() => {
        dispatch(
          setFileUploadStatus({
            dialogueSlug: dialogue_slug,
            fileType: file_type,
            status: "completed",
          }),
        );
        websocketManager.sendMessage("conversation", {
          type: "file_upload_status",
          dialogue_slug,
          file_type,
          status: "completed",
        });
        setTimeout(() => {
          dispatch(clearFileUploadStatus({ dialogueSlug: dialogue_slug }));
        }, 2000);
      }, 2000);

      uploadControllersMap.delete(dialogue_slug);

      return {
        dialogue_slug,
        message_id: response.message_id,
        file_url: response.file_url,
        file_type,
        is_encrypted_file: isEncrypted,
        user,
      };
    } catch (error: any) {
      if (error.cancelled) {
        return rejectWithValue("Upload was cancelled");
      }
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to upload file",
      );
    }
  },
);

// Cancel Upload File Thunk ----------------------------------------------------------------------
export const cancelUploadFile = createAsyncThunk(
  "conversation/cancelUploadFile",
  async (dialogueSlug: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const controller = uploadControllersMap.get(dialogueSlug);

    if (controller) {
      controller.abort();
      uploadControllersMap.delete(dialogueSlug);
    }

    dispatch(removeTemporaryMessage(dialogueSlug));
    dispatch(
      setFileUploadStatus({
        dialogueSlug,
        fileType: "file",
        status: "cancelled",
      }),
    );

    websocketManager.sendMessage("conversation", {
      type: "upload_canceled",
      dialogue_slug: dialogueSlug,
      file_type: "file",
    });

    const updatedState = getState() as RootState;
    if (updatedState.conversation.fileUploadStatus[dialogueSlug]) {
      dispatch(clearFileUploadStatus({ dialogueSlug }));
    }

    return dialogueSlug;
  },
);

// Prevent concurrent decrypts per message
const accessMediaInFlight = new Set<number>();

// Access Media Thunk ---------------------------------------------------------
export const accessMedia = createAsyncThunk<
  { messageId: number; blobUrl: string; mimeType: string },
  AccessMediaArgs,
  { rejectValue: MediaErrCode }
>(
  "conversation/accessMedia",
  async (
    { messageId, mediaType, fileName, source },
    { dispatch, rejectWithValue },
  ) => {
    const fail = (code: MediaErrCode) => rejectWithValue(code);

    // SINGLE-FLIGHT GUARD (CRITICAL)
    if (accessMediaInFlight.has(messageId)) {
      return fail("SKIP_IN_FLIGHT");
    }
    accessMediaInFlight.add(messageId);

    try {
      // 0) Skip temp / optimistic ids
      if (String(messageId).length >= 12) {
        return fail("SKIP_TEMP_ID");
      }

      // 1) Cache-first
      const cached = await getDecryptedMediaFromCache(messageId);
      if (cached?.blobUrl) {
        return {
          messageId,
          blobUrl: cached.blobUrl,
          mimeType: cached.mimeType || "application/octet-stream",
        };
      }

      // 2) Fetch envelope from backend
      const payload = await accessMediaApi(messageId, mediaType);

      const serverMime: string | undefined =
        typeof (payload as any).mime_type === "string"
          ? (payload as any).mime_type
          : undefined;

      // 3) Validate AES envelope
      const encrypted_aes_key =
        "encrypted_aes_key" in payload
          ? (payload as any).encrypted_aes_key
          : undefined;

      if (!encrypted_aes_key || !isBase64(encrypted_aes_key)) {
        return fail("MISSING_OR_INVALID_AES_KEY");
      }

      // 4) RSA-OAEP unwrap (AES key + nonce)
      const privateKey = await getPrivateKey();

      let aesKeyBytes: Uint8Array;
      let nonce: Uint8Array;

      try {
        const out = await decryptAESKeyWithPrivateKey(
          encrypted_aes_key,
          privateKey,
        );
        aesKeyBytes = out.aesKey;
        nonce = out.nonce;
      } catch {
        return fail("PRIVATE_KEY_DECRYPT_FAILED");
      }

      // 5) Fetch encrypted bytes (prefer CloudFront signed URL)
      let encryptedBytes: Uint8Array;

      // 5.1) Small payloads may come inline as base64 (keep as-is)
      if ("encrypted_file" in payload) {
        const b64 = (payload as any).encrypted_file;
        if (!isBase64(b64)) return fail("INVALID_ENCRYPTED_FILE_BASE64");
        encryptedBytes = base64ToUint8Array(b64);
      } else {
        // 5.2) Resolve CloudFront signed URL for ciphertext
        let signedUrl: string | null = null;

        try {
          const r = await resolvePlayback({
            id: `msg-enc:${messageId}:${mediaType}`, // stable cache key
            type: toPlaybackKind(mediaType), // image/video/audio/file
            target: messagePlaybackTarget(messageId), // conversation.message
            fieldName: mediaType, // must match Django field name
          });

          signedUrl = r?.url ?? null;
        } catch {
          signedUrl = null;
        }

        // 5.3) Fallback (optional): if backend still returns a download_url, use it
        const fallbackUrl =
          "download_url" in payload &&
          typeof (payload as any).download_url === "string"
            ? (payload as any).download_url
            : null;

        const urlToFetch = signedUrl || fallbackUrl;
        if (!urlToFetch) return fail("UNSUPPORTED_MEDIA_PAYLOAD");

        // 5.4) Download ciphertext as ArrayBuffer (CORS must allow your origin)
        const resp = await axios.get<ArrayBuffer>(urlToFetch, {
          responseType: "arraybuffer",
        });
        encryptedBytes = new Uint8Array(resp.data);
      }

      // 6) AES-GCM decrypt (Integrity-sensitive)
      let fileBuffer: ArrayBuffer;

      try {
        fileBuffer = await decryptFileWithAES(
          encryptedBytes,
          aesKeyBytes,
          nonce,
        );
      } catch (e: any) {
        const msg = (e?.message || "").toLowerCase();
        if (
          msg.includes("gcm") ||
          msg.includes("auth") ||
          msg.includes("operation")
        ) {
          return fail("AUTH_TAG_MISMATCH");
        }
        return fail("DECRYPT_FAILED");
      }

      // 7) Resolve MIME
      const mimeType =
        serverMime ||
        detectMimeFromBytes(fileBuffer) ||
        getMimeTypeFromMediaType(mediaType, fileName || `file.${mediaType}`);

      // 8) Cache decrypted Blob
      const blob = new Blob([fileBuffer], { type: mimeType });
      await cacheDecryptedMedia(messageId, mediaType, blob, mimeType);

      // 9) Mint object URL
      const blobUrl = URL.createObjectURL(blob);
      return { messageId, blobUrl, mimeType };
    } catch (error: any) {
      logger.error("FAILED_ACCESS_MEDIA (unexpected)", {
        messageId,
        mediaType,
        error: error?.message,
      });
      return rejectWithValue("FAILED_ACCESS_MEDIA");
    } finally {
      // always release lock
      accessMediaInFlight.delete(messageId);

      if (accessMediaInFlight.size === 0) {
        dispatch(conversationSlice.actions.markMediaHydrationDone());
      }
    }
  },
);

// Save Controller in Upload ---------------------------------------------------------------------
export const storeUploadController = createAsyncThunk(
  "conversation/storeUploadController",
  async (
    {
      dialogueSlug,
      controller,
    }: { dialogueSlug: string; controller: AbortController },
    { dispatch },
  ) => {
    dispatch(setUploadController({ dialogueSlug, controller }));
  },
);

// Clear Controller in Upload ---------------------------------------------------------------------
export const clearUploadController = createAsyncThunk(
  "conversation/clearUploadController",
  async ({ dialogueSlug }: { dialogueSlug: string }, { dispatch }) => {
    dispatch(removeUploadController({ dialogueSlug }));
  },
);

// Fetch messages ---------------------------------------------------------------------------------
export const fetchMessages = createAsyncThunk(
  "conversation/fetchMessages",
  async (
    {
      dialogueSlug,
      offset = 0,
      limit = 20,
      clearOldMessages = false,
    }: {
      dialogueSlug: string;
      offset?: number;
      limit?: number;
      clearOldMessages?: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const currentUser = state.auth?.user?.id;

      // ✅ ensure canonical device_id (key fingerprint) is available
      const deviceId = await getDeviceIdAsync();
      if (!deviceId) throw new Error("Device ID not found");

      const response = await fetchMessagesApi(dialogueSlug, offset, limit);

      // Single import of private key for performance (used only for our device)
      const privateKey = await getPrivateKey();

      const decryptedMessages = await Promise.all(
        response.messages.map(async (msg: any) => {
          const isOwnMessage = String(msg.sender?.id) === String(currentUser);

          let decrypted_content: string | null = null;
          let decrypt_error_code: DecryptErr["code"] | undefined;

          if (msg.is_encrypted_file) {
            // leave to media decrypt flow
            decrypted_content = null;
          } else {
            const meta = await resolveEncryptedContentForMessage(
              msg,
              privateKey,
              deviceId,
            );
            decrypted_content = meta.decrypted_content;
            decrypt_error_code = meta.decrypt_error_code;
          }

          return {
            ...msg,
            isOwnMessage,
            decrypted_content, // only set when we *really* have plaintext
            decrypt_error_code, // UI will render lock/CTA based on this
          };
        }),
      );

      return {
        dialogueSlug,
        messages: decryptedMessages,
        hasMore: response.has_more,
        offset,
        limit,
        clearOldMessages,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch messages",
      );
    }
  },
);

// Fetch Message By ID Thunk ----------------------------------------------------------------------
export const fetchMessageById = createAsyncThunk<
  Message,
  number,
  { rejectValue: string }
>("conversation/fetchMessageById", async (messageId, { rejectWithValue }) => {
  try {
    const message = await fetchMessageByIdApi(messageId);

    if (!message.is_encrypted || message.is_encrypted_file) {
      return {
        ...message,
        decrypted_content: message.is_encrypted_file
          ? null
          : (message.content ?? ""),
      };
    }

    const deviceId = await getDeviceIdAsync();
    if (!deviceId) throw new Error("Device ID not found");

    const privateKey = await getPrivateKey();
    const meta = await resolveEncryptedContentForMessage(
      message,
      privateKey,
      deviceId,
    );

    return {
      ...message,
      decrypted_content: meta.decrypted_content,
      decrypt_error_code: meta.decrypt_error_code,
    };
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.error || "Failed to fetch message",
    );
  }
});

// Search Messages Message Thunk ----------------------------------------------------------------------
export const searchMessages = createAsyncThunk<
  { dialogueSlug: string; results: Message[] },
  { dialogueSlug: string; query: string },
  { rejectValue: string }
>(
  "conversation/searchMessages",
  async ({ dialogueSlug, query }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const currentUser = state.auth?.user?.id;
      const dialogue = state.conversation.dialogues.find(
        (d) => d.slug === dialogueSlug,
      );
      if (!dialogue) throw new Error("Dialogue not found");

      let messages: Message[] = [];

      if (dialogue.is_group) {
        messages = await searchMessagesApi(dialogueSlug, query);
      } else {
        const response = await fetchMessagesApi(dialogueSlug, 0, 1000);
        messages = response.messages;
      }

      const deviceId = await getDeviceIdAsync();
      const privateKey = await getPrivateKey();

      const filteredMessages = await Promise.all(
        messages.map(async (msg) => {
          const isOwnMessage = String(msg.sender?.id) === String(currentUser);

          let decrypted_content: string | null = null;
          let decrypt_error_code: DecryptErr["code"] | undefined;

          if (!msg.is_encrypted || msg.is_encrypted_file) {
            decrypted_content = msg.is_encrypted_file
              ? null
              : (msg.content ?? msg.content_encrypted ?? "");
          } else {
            const meta = await resolveEncryptedContentForMessage(
              msg,
              privateKey,
              deviceId,
            );
            decrypted_content = meta.decrypted_content;
            decrypt_error_code = meta.decrypt_error_code;
          }

          // for searching: only plaintext should participate
          const searchable = (decrypted_content ?? "").toLowerCase();

          return {
            ...msg,
            isOwnMessage,
            decrypted_content, // unified field
            decrypt_error_code, // for UX
            // keep compatibility if anything in UI reads this:
            decryptedContent: decrypted_content ?? "",
            __searchable__: searchable,
          } as any;
        }),
      );

      const q = query.toLowerCase();
      const matchedResults = dialogue.is_group
        ? filteredMessages // group search API likely already filtered
        : filteredMessages.filter((m: any) => m.__searchable__.includes(q));

      // optional: strip helper field
      matchedResults.forEach((m: any) => delete m.__searchable__);

      return { dialogueSlug, results: matchedResults };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Search failed");
    }
  },
);

// Update Read Status Thunk ---------------------------------------------------------------------------------
export const updateReadStatus = createAsyncThunk(
  "conversation/updateReadStatus",
  async (
    data: { dialogueSlug: string; readerId: number; readMessages: number[] },
    { getState },
  ) => {
    const state = getState() as RootState;
    const { dialogues } = state.conversation;

    const updatedDialogues = dialogues.map((dialogue) => {
      if (dialogue.slug === data.dialogueSlug) {
        const updatedMessages = (dialogue.messages || []).map((msg) => {
          if (data.readMessages.includes(msg.id)) {
            const seenByUsers = msg.seen_by_users || [];
            if (!seenByUsers.includes(data.readerId)) {
              return {
                ...msg,
                seen_by_users: [...seenByUsers, data.readerId],
              };
            }
          }
          return msg;
        });

        return {
          ...dialogue,
          messages: updatedMessages,
        };
      }
      return dialogue;
    });

    return { dialogues: updatedDialogues };
  },
);

// Mark Message As Delivered ---------------------------------------------------------------------------------
export const markMessageAsDelivered = createAsyncThunk<
  { dialogueSlug: string; messageId: number } | null,
  {
    dialogueSlug: string;
    messageId: number;
    userId: number;
    isDelivered: boolean;
  },
  { state: RootState }
>("conversation/markMessageAsDelivered", async (data, { getState }) => {
  const state = getState();
  const me = state.auth.user?.id;
  const dlg = state.conversation.dialogues.find(
    (d) => d.slug === data.dialogueSlug,
  );
  const msg = dlg?.messages?.find((m) => m.id === data.messageId);

  // Guard invalid states
  if (!me || !dlg || !msg) return null;

  // Recipient only
  if (msg.sender?.id === me) return null;

  // Idempotent
  if (msg.is_delivered) return null;

  // Send authoritative ack to backend
  if (websocketManager.isConnected()) {
    websocketManager.sendMessage("conversation", {
      type: "mark_as_delivered",
      dialogue_slug: data.dialogueSlug,
      message_id: data.messageId,
    });
  } else {
    await markMessageAsDeliveredApi({
      dialogue_slug: data.dialogueSlug,
      message_id: data.messageId,
    });
  }

  // Immediate local sync; backend echo may arrive later too
  return { dialogueSlug: data.dialogueSlug, messageId: data.messageId };
});

// Mark Message As Read ------------------------------------------------------------------------------------
export const markMessagesAsRead = createAsyncThunk(
  "conversation/markMessagesAsRead",
  async (dialogueSlug: string, { getState }) => {
    const state = getState() as RootState;
    const dialogue = state.conversation.dialogues.find(
      (d) => d.slug === dialogueSlug,
    );
    const currentUserId = state.auth.user?.id;

    if (!dialogue?.messages || !currentUserId) return null;

    // Only mark incoming unread messages
    const unreadMessages = dialogue.messages.filter(
      (msg) =>
        msg.sender?.id !== currentUserId &&
        Array.isArray(msg.seen_by_users) &&
        !msg.seen_by_users.includes(currentUserId),
    );

    if (unreadMessages.length === 0) {
      return null;
    }

    const readMessageIds = unreadMessages.map((msg) => msg.id);

    try {
      if (websocketManager.isConnected()) {
        websocketManager.sendMessage("conversation", {
          type: "mark_as_read",
          dialogue_slug: dialogueSlug,
          read_messages: readMessageIds,
          source: "frontend",
        });
      } else {
        await markMessagesAsReadApi(dialogueSlug, readMessageIds);
      }

      // Return optimistic-but-authoritative local update payload.
      // Backend/WS can still echo the same read receipt later.
      return {
        dialogueSlug,
        readerId: currentUserId,
        readMessages: readMessageIds,
      };
    } catch (error: any) {
      logger.error("Failed to mark messages as read", {
        dialogueSlug,
        readerId: currentUserId,
        error: error?.message,
        status: error?.response?.status,
      });
      throw error;
    }
  },
);

// Fetch Seen By Users Thunk -------------------------------------------------------
export const fetchSeenByUsers = createAsyncThunk<
  { messageId: number; users: SeenUser[] },
  number
>("conversation/fetchSeenByUsers", async (messageId, { rejectWithValue }) => {
  try {
    const users = await getSeenByUsersApi(messageId);
    return { messageId, users };
  } catch (error) {
    return rejectWithValue("Failed to fetch seen-by users");
  }
});

// Voice and Video Recording -----------------------------------------------------
export const setRecordingStatus = createAsyncThunk(
  "conversation/setRecordingStatus",
  async (
    data: {
      dialogueSlug: string;
      sender: Participant;
      isRecording: boolean;
      fileType: "audio" | "video";
    },
    { dispatch },
  ) => {
    dispatch(
      setRecording({
        dialogueSlug: data.dialogueSlug,
        sender: data.sender,
        fileType: data.fileType,
        isRecording: data.isRecording ?? false,
      }),
    );

    if (!data.isRecording) {
      setTimeout(() => {
        dispatch(clearRecordingStatus({ dialogueSlug: data.dialogueSlug }));
      }, 3000);
    }
  },
);

// ✅ پاکسازی تمامی وضعیت‌ها
export const clearAllStatuses = () => (dispatch: AppDispatch) => {
  dispatch(resetAllTypingStatuses());
  dispatch(resetAllRecordingStatuses());
  dispatch(resetAllFileUploadStatuses());
};

// ✅ پاکسازی وضعیت تایپ کردن
export const resetAllTypingStatuses = () => (dispatch: AppDispatch) => {
  dispatch({ type: "conversation/clearAllTypingStatuses" });
};

// ✅ پاکسازی وضعیت ضبط
export const resetAllRecordingStatuses = () => (dispatch: AppDispatch) => {
  dispatch({ type: "conversation/clearAllRecordingStatuses" });
};

// ✅ پاکسازی وضعیت آپلود فایل
export const resetAllFileUploadStatuses = () => (dispatch: AppDispatch) => {
  dispatch({ type: "conversation/clearAllFileUploadStatuses" });
};

// Group Thunks ---------------------------------------------------------------------------------------------
// Create Group Chat -----------------------------------------------------------
export const createGroup = createAsyncThunk(
  "conversation/createGroup",
  async (data: { name: string; image?: File }, thunkAPI) => {
    try {
      const response = await createGroupApi(data);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || "Failed to create group",
      );
    }
  },
);

// Update Group Image Thunk -----------------------------------------------------
export const updateGroupImage = createAsyncThunk(
  "conversation/updateGroupImage",
  async (
    { dialogueSlug, imageFile }: { dialogueSlug: string; imageFile: File },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateGroupImageApi({ dialogueSlug, imageFile });
      return {
        dialogueSlug,
        message: response.detail,
        previewUrl: URL.createObjectURL(imageFile),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Image update failed",
      );
    }
  },
);

// Update Group Info (name/info/bio) -----------------------------------------------------
export const updateGroupInfo = createAsyncThunk(
  "conversation/updateGroupInfo",
  async (
    {
      dialogueSlug,
      data, // { group_name?: string; info?: string; bio?: string }
    }: {
      dialogueSlug: string;
      data: Partial<{ group_name: string; info: string; bio: string }>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateGroupInfoApi({ dialogueSlug, data });
      // Server returns: { detail, updated: {group_name?, group_info?, group_bio?}, unsupported: [] }
      return {
        dialogueSlug,
        updated: response.updated || {},
        detail: response.detail,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.detail || "Group info update failed",
      );
    }
  },
);

// Delete Group Chat -----------------------------------------------------------
export const smartDeleteDialogue = createAsyncThunk(
  "conversation/smartDeleteDialogue",
  async (dialogueSlug: string, thunkAPI) => {
    try {
      const response = await smartDeleteDialogueApi(dialogueSlug);
      return { dialogueSlug, ...response };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || "Failed to delete dialogue.",
      );
    }
  },
);

// Fetch Dialogue By Id ---------------------------------------------------------
export const fetchDialogueBySlug = createAsyncThunk(
  "conversation/fetchDialogueBySlug",
  async (dialogueSlug: string, thunkAPI) => {
    try {
      const response = await fetchDialogueBySlugApi(dialogueSlug);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || "Failed to fetch dialogue.",
      );
    }
  },
);

// Add Participant in Group ------------------------------------------------------
export const addParticipant = createAsyncThunk(
  "conversation/addParticipant",
  async (
    {
      dialogueSlug,
      participantId,
    }: { dialogueSlug: string; participantId: string },
    thunkAPI,
  ) => {
    try {
      await addParticipantApi(dialogueSlug, participantId);
      thunkAPI.dispatch(fetchDialogueBySlug(dialogueSlug));
      return { dialogueSlug, participantId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || "Failed to add participant.",
      );
    }
  },
);

// Remove Participant in Group ---------------------------------------------------------
export const removeParticipant = createAsyncThunk(
  "conversation/removeParticipant",
  async (
    {
      dialogueSlug,
      participantId,
    }: { dialogueSlug: string; participantId: string },
    thunkAPI,
  ) => {
    try {
      await removeParticipantApi(dialogueSlug, participantId);
      thunkAPI.dispatch(fetchDialogueBySlug(dialogueSlug));
      return { dialogueSlug, participantId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || "Failed to remove participant.",
      );
    }
  },
);

// ✅ thunk to fetch group participants
export const fetchGroupParticipants = createAsyncThunk(
  "conversation/fetchGroupParticipants",
  async (dialogueSlug: string, thunkAPI) => {
    try {
      const participants = await fetchGroupParticipantsApi(dialogueSlug);
      return { dialogueSlug, participants };
    } catch (error: any) {
      return thunkAPI.rejectWithValue("Failed to fetch group participants.");
    }
  },
);

// Promote To Elder Thunk --------------------------------
export const promoteToElder = createAsyncThunk<
  { dialogueSlug: string; userId: number },
  { dialogueSlug: string; userId: number },
  { rejectValue: string }
>("conversation/promoteToElder", async ({ dialogueSlug, userId }, thunkAPI) => {
  try {
    await promoteToElderApi(dialogueSlug, userId.toString());
    thunkAPI.dispatch(fetchGroupParticipants(dialogueSlug));
    return { dialogueSlug, userId };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error || "Failed to promote user.",
    );
  }
});

// Demote To Participant Thunk ---------------------------
export const demoteToParticipant = createAsyncThunk<
  { dialogueSlug: string; userId: number },
  { dialogueSlug: string; userId: number },
  { rejectValue: string }
>(
  "conversation/demoteToParticipant",
  async ({ dialogueSlug, userId }, thunkAPI) => {
    try {
      await demoteToParticipantApi(dialogueSlug, userId.toString());
      thunkAPI.dispatch(fetchGroupParticipants(dialogueSlug));
      return { dialogueSlug, userId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error || "Failed to demote user.",
      );
    }
  },
);

// Resign Elder Role Thunk ---------------------------
export const resignElderRole = createAsyncThunk<
  { dialogueSlug: string },
  string, // 👈 slug
  { rejectValue: string }
>("conversation/resignElderRole", async (dialogueSlug, thunkAPI) => {
  try {
    await resignElderRoleApi(dialogueSlug);
    thunkAPI.dispatch(fetchGroupParticipants(dialogueSlug));
    return { dialogueSlug };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error || "Failed to resign from Elder role.",
    );
  }
});

// Leave Group Thunk -------------------------------------
export const leaveGroup = createAsyncThunk<
  string, // returned: slug
  string, // passed: slug
  { rejectValue: string }
>("conversation/leaveGroup", async (dialogueSlug, thunkAPI) => {
  try {
    await leaveGroupApi(dialogueSlug);
    return dialogueSlug;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error || "Failed to leave group",
    );
  }
});

// Transfer Founder Thunk -----------------------------
export const transferFounder = createAsyncThunk<
  { message: string },
  { dialogueSlug: string; userId: number },
  { rejectValue: string }
>(
  "conversation/transferFounder",
  async ({ dialogueSlug, userId }, { rejectWithValue }) => {
    try {
      const response = await transferFounderApi(dialogueSlug, userId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to transfer founder role",
      );
    }
  },
);

// Select Group Participants -----------------------------
export const selectGroupParticipants = createSelector(
  [
    (state: RootState) => state.conversation.groupParticipants,
    (_: RootState, dialogueSlug: string) => dialogueSlug,
  ],
  (groupParticipants, dialogueSlug) => {
    return groupParticipants[dialogueSlug] || [];
  },
);

// Slice -----------------------------------------------------------------------------------------------------
const conversationSlice = createSlice({
  name: "conversation",
  initialState,
  reducers: {
    // Add Message  ========================================
    addMessage: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        message: Message;
        currentUserId: number;
      }>,
    ) => {
      const { dialogueSlug, message, currentUserId } = action.payload;

      // 1) Try find existing dialogue
      let dialogueIndex = state.dialogues.findIndex(
        (d) => d.slug === dialogueSlug,
      );
      let dialogue =
        dialogueIndex !== -1 ? state.dialogues[dialogueIndex] : undefined;

      // 2) If not found → create one
      if (!dialogue) {
        const newDialogue: Dialogue = {
          slug: dialogueSlug,
          group_name: `Chat ${dialogueSlug}`,
          participants: [],
          is_group: false,
          created_at: new Date().toISOString(),
          last_message: undefined,
          messages: [],
          hasMoreMessages: true,
          group_avatar_url: null,
          group_avatar_cdn_url: null,
        };

        state.dialogues.push(newDialogue);
        dialogueIndex = state.dialogues.length - 1;
        dialogue = state.dialogues[dialogueIndex];
      }

      // 3) Ensure message list exists
      if (!dialogue.messages) {
        dialogue.messages = [];
      }

      // 4) Already exists?
      const existingMessageIndex = dialogue.messages.findIndex(
        (msg) => String(msg.id) === String(message.id),
      );

      if (existingMessageIndex !== -1) {
        dialogue.messages[existingMessageIndex] = {
          ...dialogue.messages[existingMessageIndex],
          ...message,
          isOwnMessage: message.sender.id === currentUserId,
        };

        // Move active dialogue to top on real-time update
        if (dialogueIndex > 0) {
          const [movedDialogue] = state.dialogues.splice(dialogueIndex, 1);
          state.dialogues.unshift(movedDialogue);
        }
        return;
      }

      // 5) Check temp message merge (optimistic UI)
      const tempMessageIndex = dialogue.messages.findIndex(
        (msg) =>
          msg.isOwnMessage &&
          msg.tempId &&
          msg.decrypted_content === message.decrypted_content &&
          Math.abs(
            new Date(msg.timestamp).getTime() -
              new Date(message.timestamp).getTime(),
          ) < 5000,
      );

      if (tempMessageIndex !== -1) {
        dialogue.messages[tempMessageIndex] = {
          ...message,
          isOwnMessage: true,
          tempId: null,
        };

        // Move active dialogue to top on real-time update
        if (dialogueIndex > 0) {
          const [movedDialogue] = state.dialogues.splice(dialogueIndex, 1);
          state.dialogues.unshift(movedDialogue);
        }
        return;
      }

      // 6) Normal push
      dialogue.messages.push({
        ...message,
        isOwnMessage: message.sender.id === currentUserId,
      });

      // 7) Move updated dialogue to top
      if (dialogueIndex > 0) {
        const [movedDialogue] = state.dialogues.splice(dialogueIndex, 1);
        state.dialogues.unshift(movedDialogue);
      }
    },

    // Update Message ========================================
    updateMessage: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        tempMessageId: number;
        newMessageId: number;
        patch?: Partial<Message>;
      }>,
    ) => {
      const { dialogueSlug, tempMessageId, newMessageId, patch } =
        action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);

      if (!dialogue || !dialogue.messages) {
        logger.warn("Dialogue not found for updateMessage", { dialogueSlug });
        return;
      }

      const idx = dialogue.messages.findIndex(
        (msg) => msg.id === tempMessageId,
      );
      if (idx === -1) {
        logger.warn("Temp message not found for updateMessage", {
          dialogueSlug,
          tempMessageId,
        });
        return;
      }

      // Merge existing message with new id, delivery flag, and optional patch
      const current = dialogue.messages[idx];
      dialogue.messages[idx] = {
        ...current,
        id: newMessageId,
        is_delivered: true,
        ...(patch ?? {}),
      };
    },

    // Edit Message ========================================
    updateEditedMessage: (
      state,
      action: PayloadAction<{
        message_id: number;
        dialogue_slug: string;
        new_content: string;
        edited_at: string;
        is_edited: boolean;
      }>,
    ) => {
      const { message_id, dialogue_slug, new_content, edited_at, is_edited } =
        action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
      if (!dialogue || !dialogue.messages) return;

      const message = dialogue.messages.find(
        (msg) => String(msg.id) === String(message_id),
      );
      if (message) {
        message.decrypted_content = new_content;
        message.edited_at = edited_at;
        message.is_edited = is_edited;
      }

      // Keep dialogue preview source in sync with the edited message
      const lastMessage: any = dialogue.last_message as any;
      if (lastMessage && String(lastMessage.id) === String(message_id)) {
        lastMessage.decrypted_content = new_content;
        lastMessage.edited_at = edited_at;
        lastMessage.is_edited = is_edited;
      }
    },

    // Update Last Message =================================
    updateLastMessage: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        lastMessage: Dialogue["last_message"];
      }>,
    ) => {
      const { dialogueSlug, lastMessage } = action.payload;

      const dialogueIndex = state.dialogues.findIndex(
        (d) => d.slug === dialogueSlug,
      );
      if (dialogueIndex === -1) return;

      const dialogue = state.dialogues[dialogueIndex];

      // Never let system messages overwrite list preview
      const isSystemLike =
        !!(lastMessage as any)?.is_system ||
        (lastMessage as any)?.event_type === "system_message";

      if (isSystemLike) return;

      dialogue.last_message = lastMessage;

      // Move updated dialogue to top
      if (dialogueIndex > 0) {
        const [movedDialogue] = state.dialogues.splice(dialogueIndex, 1);
        state.dialogues.unshift(movedDialogue);
      }
    },

    // Scroll To Message Id =================================
    setScrollToMessageId(state, action) {
      state.scrollToMessageId = action.payload;
    },
    clearScrollToMessageId(state) {
      state.scrollToMessageId = null;
    },
    setHighlightedMessageId(state, action) {
      state.highlightedMessageId = action.payload;
    },
    clearHighlightedMessageId(state) {
      state.highlightedMessageId = null;
    },

    // Message Soft Delete ====================================
    messageSoftDeleted: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        messageId: number;
        userId: number;
      }>,
    ) => {
      const { dialogueSlug, messageId, userId } = action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (!dialogue || !dialogue.messages) return;

      const message = dialogue.messages.find((m) => m.id === messageId);
      if (message) {
        if (!message.deleted_by_users) {
          message.deleted_by_users = [userId];
        } else if (!message.deleted_by_users.includes(userId)) {
          message.deleted_by_users.push(userId);
        }
      }
    },

    // Message Hard Delete ====================================
    messageHardDeleted: (
      state,
      action: PayloadAction<{ dialogueSlug: string; messageId: number }>,
    ) => {
      const { dialogueSlug, messageId } = action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (!dialogue || !dialogue.messages) return;

      dialogue.messages = dialogue.messages.filter((m) => m.id !== messageId);
    },

    // Remove Temporary Message ==================================
    removeTemporaryMessage: (state, action: PayloadAction<string>) => {
      const dialogueSlug = action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (!dialogue || !dialogue.messages) return;

      dialogue.messages = dialogue.messages.filter((msg) => !msg.isTemporary);
    },

    // Typing Status =============================================
    setTypingStatus: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        sender: Participant;
        isTyping: boolean;
      }>,
    ) => {
      const { dialogueSlug, sender, isTyping } = action.payload;

      if (!state.typingStatus) state.typingStatus = {};

      if (isTyping) {
        state.typingStatus[dialogueSlug] = sender.username;
      } else {
        delete state.typingStatus[dialogueSlug];
      }
    },

    // Update Typing Status
    updateTypingStatus: (
      state,
      action: PayloadAction<{
        dialogue_slug: string;
        sender: Participant;
        is_typing: boolean;
      }>,
    ) => {
      const { dialogue_slug, sender, is_typing } = action.payload;

      if (!state.typingStatus) state.typingStatus = {};

      if (is_typing) {
        state.typingStatus[dialogue_slug] = sender.username;
      } else {
        delete state.typingStatus[dialogue_slug];
      }
    },

    // Clear Typing Status
    clearTypingStatus: (
      state,
      action: PayloadAction<{ dialogueSlug: string }>,
    ) => {
      const { dialogueSlug } = action.payload;
      delete state.typingStatus[dialogueSlug];
    },

    clearAllTypingStatuses: (state) => {
      state.typingStatus = {};
    },

    // Recording Status ======================================
    setRecording: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        sender: Participant;
        fileType: "audio" | "video";
        isRecording: boolean;
      }>,
    ) => {
      const { dialogueSlug, sender, fileType, isRecording } = action.payload;
      state.recordingStatus[dialogueSlug] = { sender, fileType, isRecording };
    },

    // clear Recording Status
    clearRecordingStatus: (
      state,
      action: PayloadAction<{ dialogueSlug: string }>,
    ) => {
      delete state.recordingStatus[action.payload.dialogueSlug];
    },

    // clear All Recording Status
    clearAllRecordingStatuses: (state) => {
      state.recordingStatus = {};
    },

    // Set File Status ======================================
    setFileUploadStatus: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        fileType: "image" | "video" | "audio" | "file";
        status: string;
        progress?: number | null;
      }>,
    ) => {
      const {
        dialogueSlug,
        fileType,
        status,
        progress = null,
      } = action.payload;
      state.fileUploadStatus[dialogueSlug] = { fileType, status, progress };
    },

    // Clear File Status
    clearFileUploadStatus: (
      state,
      action: PayloadAction<{ dialogueSlug: string }>,
    ) => {
      delete state.fileUploadStatus[action.payload.dialogueSlug];
    },

    // Clear All File Status
    clearAllFileUploadStatuses: (state) => {
      state.fileUploadStatus = {};
    },

    // Set Upload Controller =================================
    setUploadController: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        controller: AbortController;
      }>,
    ) => {
      state.uploadControllers[action.payload.dialogueSlug] =
        action.payload.controller;
    },

    // Remove Upload Controller
    removeUploadController: (
      state,
      action: PayloadAction<{ dialogueSlug: string }>,
    ) => {
      delete state.uploadControllers[action.payload.dialogueSlug];
    },

    // Lsat Seen ============================================
    updateLastSeen: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        userId: number;
        is_online: boolean;
        last_seen: string | null;
        last_seen_epoch?: number | null;
        last_seen_display?: string;
      }>,
    ) => {
      const {
        dialogueSlug,
        userId,
        is_online,
        last_seen,
        last_seen_epoch,
        last_seen_display,
      } = action.payload;

      const epochFromIso =
        !last_seen_epoch && last_seen
          ? Math.floor(new Date(last_seen).getTime() / 1000)
          : (last_seen_epoch ?? null);

      state.lastSeenMap[dialogueSlug] = {
        user_id: userId,
        is_online: !!is_online,
        last_seen: last_seen ?? null,
        last_seen_epoch: epochFromIso,
      };

      // @ts-ignore
      state.lastSeenMap[dialogueSlug].last_seen_display =
        last_seen_display as any;
    },

    // Set Message Delivered =====================================
    setMessageDelivered: (state, action) => {
      const { dialogueSlug, messageId } = action.payload;

      const dlg = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (!dlg || !dlg.messages) return;

      const idx = dlg.messages.findIndex(
        (m) => String(m.id) === String(messageId),
      );
      if (idx === -1) return;

      // 🔥 IMPORTANT: mutate in place (do NOT replace the object)
      dlg.messages[idx].is_delivered = true;
    },

    // Add New Dialogue =====================================
    addNewDialogue: (state, action: PayloadAction<Dialogue>) => {
      const exists = state.dialogues.some(
        (d) => d.slug === action.payload.slug,
      );
      if (!exists) {
        state.dialogues.push({
          ...action.payload,
          messages: [],
        });
      }
    },

    // Remove New Dialogue ===================================
    removeDialogue: (state, action: PayloadAction<string>) => {
      const dialogueSlug = action.payload;
      state.dialogues = state.dialogues.filter((d) => d.slug !== dialogueSlug);
    },

    // Remove User From Dialogue =============================
    removeParticipantLocally: (
      state,
      action: PayloadAction<{ dialogueSlug: string; userId: number }>,
    ) => {
      const { dialogueSlug, userId } = action.payload;
      const participants = state.groupParticipants[dialogueSlug];
      if (participants) {
        state.groupParticipants[dialogueSlug] = participants.filter(
          (p) => p.user.id !== userId,
        );
      }
    },

    // Update My Role ========================================
    updateMyRole: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        newRole: "founder" | "elder" | "participant";
      }>,
    ) => {
      const { dialogueSlug, newRole } = action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (dialogue) {
        dialogue.my_role = newRole;
      }
    },

    // Unread Count ==========================================
    incrementUnreadCount: (state, action: PayloadAction<string>) => {
      const dialogueSlug = action.payload;
      if (!state.unreadCounts[dialogueSlug]) {
        state.unreadCounts[dialogueSlug] = 1;
      } else {
        state.unreadCounts[dialogueSlug]++;
      }
    },

    resetUnreadCount: (state, action: PayloadAction<string>) => {
      const dialogueSlug = action.payload;
      state.unreadCounts[dialogueSlug] = 0;
    },

    updateUnreadCount: (
      state,
      action: PayloadAction<{ dialogueSlug: string; count: number }>,
    ) => {
      const { dialogueSlug, count } = action.payload;
      state.unreadCounts[dialogueSlug] = count;
    },

    updateDialogueMarker: (
      state,
      action: PayloadAction<{
        dialogueSlug: string;
        is_sensitive: boolean;
        marker_id: number | null;
      }>,
    ) => {
      const { dialogueSlug, is_sensitive, marker_id } = action.payload;
      const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
      if (dialogue) {
        dialogue.is_sensitive = is_sensitive;
        dialogue.marker_id = marker_id;
      }
    },

    markMediaHydrationDone: (state) => {
      state.mediaHydrationDone = true;
    },
  },

  extraReducers: (builder) => {
    builder

      // Create New Dialogue --------------------------------------------
      .addCase(createNewDialogue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewDialogue.fulfilled, (state, action) => {
        state.loading = false;

        const newDialogue = action.payload.dialogue;
        if (!newDialogue) return; // در حالت check_only اگر دیالوگی وجود نداشته باشد

        const exists = state.dialogues.some((d) => d.slug === newDialogue.slug);
        if (!exists) {
          state.dialogues.unshift(newDialogue);
        }
      })
      .addCase(createNewDialogue.rejected, (state, action) => {
        state.loading = false;

        // اگر دیالوگ وجود نداشت (status 204)، ارور واقعی نیست → ثبت نکن
        if (action.payload?.code === 204) {
          state.error = null;
        } else {
          state.error = action.payload?.message || "Failed to create dialogue";
        }
      })

      // Mark Dialogue --------------------------------------------
      .addCase(markDialogueAsSensitive.fulfilled, (state, action) => {
        const index = state.userDialogueMarkers.findIndex(
          (marker) => marker.dialogue_slug === action.payload.dialogue_slug,
        );

        if (index !== -1) {
          state.userDialogueMarkers[index] = action.payload;
        } else {
          state.userDialogueMarkers.push(action.payload);
        }
      })

      // Unmark Dialogue ------------------------------------------
      .addCase(unmarkDialogueAsSensitive.fulfilled, (state, action) => {
        state.userDialogueMarkers = state.userDialogueMarkers.filter(
          (marker) => marker.id !== action.payload,
        );
      })

      // Enter Chat ------------------------------------------------
      .addCase(enterChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enterChat.fulfilled, (state, action) => {
        state.loading = false;
        state.dialogues = action.payload.dialogues;
      })
      .addCase(enterChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Last Seen -----------------------------------------------------------------------
      .addCase(fetchLastSeen.fulfilled, (state, action) => {
        const { dialogueSlug, lastSeenData } = action.payload;

        const userId = Number(lastSeenData?.user_id);
        const isOnline = !!lastSeenData?.is_online;
        const last_seen = lastSeenData?.last_seen ?? null;
        const last_seen_epoch = lastSeenData?.last_seen_epoch ?? null;
        const last_seen_display = lastSeenData?.last_seen_display;

        const epochFromIso =
          !last_seen_epoch && last_seen
            ? Math.floor(new Date(last_seen).getTime() / 1000)
            : (last_seen_epoch ?? null);

        state.lastSeenMap[dialogueSlug] = {
          user_id: userId,
          is_online: isOnline,
          last_seen,
          last_seen_epoch: epochFromIso,
          // @ts-ignore
          last_seen_display: last_seen_display as any,
        };
      })

      // Unread Counts ------------------------------------------------------------------
      .addCase(fetchUnreadCounts.fulfilled, (state, action) => {
        action.payload.forEach(({ dialogue_slug, unread_count }) => {
          if (!dialogue_slug) return;

          state.unreadCounts[dialogue_slug] = Number(unread_count ?? 0);
        });
      })

      // Handle sendMessage --------------------------------------------------------------
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        const {
          dialogue_slug,
          content,
          is_encrypted,
          user,
          tempId,
          message_id,
        } = action.payload;

        if (!dialogue_slug) {
          logger.warn("Missing `dialogue_slug` in sendMessage response");
          return;
        }

        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (dialogue) {
          const messages = dialogue.messages || [];
          const alreadyExists = messages.some((msg) => {
            const msgId = Number(msg.id) || 0;
            return msgId === Number(tempId) || msgId === Number(message_id);
          });

          if (alreadyExists) {
            logger.warn("Duplicate message detected, skipping");
            return;
          }

          const newMessage: Message = {
            id: message_id ?? Date.now(),
            tempId: tempId ?? null,
            dialogue: dialogue_slug,
            sender: {
              id: user.id,
              username: user.username,
              email: user.email,
            },
            timestamp: new Date().toISOString(),
            seen_by_users: [],
            is_delivered: false,
            is_encrypted: is_encrypted || false,
            decrypted_content: content,
            isOwnMessage: true,
            isTemporary: false,
          };

          if (!dialogue.messages) {
            dialogue.messages = [];
          }

          dialogue.messages.push(newMessage);
        } else {
          logger.warn("Dialogue not found when pushing new message", {
            dialogue_slug,
          });
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit Message ---------------------------------------------------------------------------------
      .addCase(editMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { message_id, dialogue_slug, new_content, edited_at } =
          action.payload;
        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (!dialogue || !dialogue.messages) return;

        const message = dialogue.messages.find((msg) => msg.id === message_id);
        if (message) {
          message.decrypted_content = new_content;
          message.edited_at = edited_at;
          message.is_edited = true;
        }
      })

      .addCase(editMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Soft Delete Message ---------------------------------------------------------------------------------
      .addCase(softDeleteMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(softDeleteMessage.fulfilled, (state, action) => {
        state.loading = false;

        const { dialogue_slug, message_id, user_id } = action.payload;
        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (!dialogue || !dialogue.messages) return;

        const message = dialogue.messages.find((msg) => msg.id === message_id);
        if (message && !message.deleted_by_users?.includes(user_id)) {
          message.deleted_by_users = [
            ...(message.deleted_by_users || []),
            user_id,
          ];
        }
      })
      .addCase(softDeleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Hard Delete Message ---------------------------------------------------------------------------------
      .addCase(hardDeleteMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(hardDeleteMessage.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.viaSocket) {
          return;
        }

        const { dialogue_slug, message_id } = action.payload;
        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (!dialogue || !dialogue.messages) return;

        dialogue.messages = dialogue.messages.filter(
          (msg) => msg.id !== message_id,
        );
      })
      .addCase(hardDeleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Upload File ---------------------------------------------------------------------------------
      .addCase(uploadFile.pending, (state, action) => {
        const { dialogue_slug } = action.meta.arg;
        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (!dialogue) {
          state.loading = true;
          return;
        }

        if (!dialogue.messages) {
          dialogue.messages = [];
        }

        if (dialogue.messages.length === 0) {
          state.loading = true;
        }
      })

      .addCase(uploadFile.fulfilled, (state, action) => {
        state.loading = false;
        const {
          dialogue_slug,
          message_id,
          file_type,
          user,
          is_encrypted_file,
        } = action.payload;

        const dialogue = state.dialogues.find((d) => d.slug === dialogue_slug);
        if (!dialogue) return;

        if (!dialogue.messages) {
          dialogue.messages = [];
        }

        // const tempMessageIndex = dialogue.messages.findIndex((msg) => !msg.id);
        const tempMessageIndex = dialogue.messages.findIndex(
          (msg) => msg.isTemporary,
        );
        if (tempMessageIndex !== -1) {
          dialogue.messages[tempMessageIndex] = {
            ...dialogue.messages[tempMessageIndex],
            id: message_id,
            is_delivered: true,
            isTemporary: false,
            is_encrypted_file: is_encrypted_file,
          };
        }

        if (state.uploadControllers[dialogue_slug]) {
          delete state.uploadControllers[dialogue_slug];
        }
      })

      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Cancel Upload File ---------------------------------------------------------------------------------
      .addCase(cancelUploadFile.pending, (state, action) => {
        const dialogueId = action.meta.arg;
        if (state.fileUploadStatus[dialogueId]) {
          state.fileUploadStatus[dialogueId].status = "cancelling";
        }
      })
      .addCase(cancelUploadFile.fulfilled, (state, action) => {
        const dialogueSlug = action.payload;

        // Delete Upload Status
        if (state.fileUploadStatus[dialogueSlug]) {
          delete state.fileUploadStatus[dialogueSlug];
        }

        // Delete Controller
        if (state.uploadControllers[dialogueSlug]) {
          delete state.uploadControllers[dialogueSlug];
        }

        // Delete Temporary Message
        const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (dialogue && dialogue.messages) {
          dialogue.messages = dialogue.messages.filter(
            (msg) => !msg.isTemporary,
          );
        }
      })
      .addCase(cancelUploadFile.rejected, (state, action) => {
        const dialogueId = action.meta.arg;
        if (state.fileUploadStatus[dialogueId]) {
          state.fileUploadStatus[dialogueId].status = "failed_to_cancel";
        }
      })

      // Access Media -------------------------------------------------------------------------
      .addCase(accessMedia.pending, (state) => {
        // no-op: media decrypt is per-message; don't toggle global loading
      })
      .addCase(accessMedia.fulfilled, (state, action) => {
        const { messageId, blobUrl } = action.payload;
        if (!state.mediaBlobs) state.mediaBlobs = {};
        state.mediaBlobs[messageId] = blobUrl;
        // no global loading/error changes
      })
      .addCase(accessMedia.rejected, (state, action) => {
        // ❌ DO NOT set state.error here (prevents global Alert spam)
        // Optional (debug): keep a per-message error map if you want
        // const id = action.meta?.arg?.messageId;
        // if (id) {
        //   state.mediaErrors ??= {};
        //   state.mediaErrors[id] = action.payload || "FAILED_ACCESS_MEDIA";
        // }
      })

      // Fetch messages ------------------------------------------------------------------
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        const {
          dialogueSlug,
          messages,
          hasMore,
          offset,
          limit,
          clearOldMessages,
        } = action.payload;

        let dialogueIndex = state.dialogues.findIndex(
          (d) => d.slug === dialogueSlug,
        );

        if (dialogueIndex === -1) {
          state.dialogues.push({
            slug: dialogueSlug,
            group_name: `Chat ${dialogueSlug}`,
            participants: [],
            is_group: false,
            created_at: new Date().toISOString(),
            last_message: undefined,
            messages: [],
            hasMoreMessages: true,
            group_avatar_url: null,
            group_avatar_cdn_url: null,
          });

          dialogueIndex = state.dialogues.length - 1;
        }

        const dialogue = state.dialogues[dialogueIndex];

        if (!dialogue.messages) {
          dialogue.messages = [];
        }

        if (clearOldMessages) {
          dialogue.messages = messages;
        } else {
          const existingMessageIds = new Set(
            dialogue.messages.map((msg) => msg.id),
          );
          const newMessages = messages.filter(
            (msg) => !existingMessageIds.has(msg.id),
          );

          dialogue.messages = [...dialogue.messages, ...newMessages];
        }

        dialogue.hasMoreMessages = hasMore;

        if (!clearOldMessages) {
          return;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch message By ID --------------------------------------------------------------
      .addCase(fetchMessageById.fulfilled, (state, action) => {
        const incoming = action.payload;

        // ✅ dialogue field might be numeric from API, but our store uses slug.
        // If your API returns numeric dialogue id, we still update by searching message id.
        // This is the most robust approach.
        for (const d of state.dialogues) {
          if (!Array.isArray(d.messages)) d.messages = [];

          const idx = d.messages.findIndex(
            (m) => String(m.id) === String(incoming.id),
          );
          if (idx !== -1) {
            // ✅ merge: keep local UI fields, overwrite with API URLs/flags
            d.messages[idx] = {
              ...(d.messages[idx] as any),
              ...(incoming as any),
            };
            return;
          }
        }

        // Fallback: if not found anywhere, try dialogue slug/id direct
        const dialogueKey = incoming.dialogue as any;
        const dialogue = state.dialogues.find((d) => d.slug === dialogueKey);
        if (!dialogue) return;

        if (!Array.isArray(dialogue.messages)) dialogue.messages = [];
        dialogue.messages.push(incoming);
      })

      .addCase(fetchMessageById.rejected, (state, action) => {
        logger.error("Failed to fetch message by ID", {
          error: action.payload,
        });
      })

      // Search Messages ------------------------------------------------------
      .addCase(searchMessages.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchMessages.fulfilled, (state, action) => {
        const { dialogueSlug, results } = action.payload;
        state.searchResults[dialogueSlug] = results;
        state.searchLoading = false;
      })
      .addCase(searchMessages.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload || "Search failed";
      })

      // Mark Messages as Read -----------------------------------------------
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload) return;

        const { dialogueSlug, readerId, readMessages } = payload;
        const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (!dialogue || !dialogue.messages) return;

        dialogue.messages = dialogue.messages.map((msg) => {
          if (!readMessages.includes(msg.id)) return msg;

          const seenBy = Array.isArray(msg.seen_by_users)
            ? msg.seen_by_users
            : [];
          if (seenBy.includes(readerId)) return msg;

          return {
            ...msg,
            seen_by_users: [...seenBy, readerId],
          };
        });
      })

      // Update Read Status --------------------------------------------------
      .addCase(updateReadStatus.fulfilled, (state, action) => {
        const updatedDialogues = action.payload.dialogues;
        state.dialogues = updatedDialogues;
      })

      // Mark Messages as Delivered ------------------------------------------
      .addCase(markMessageAsDelivered.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload) return;

        const { dialogueSlug, messageId } = payload;
        const dlg = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (!dlg || !dlg.messages) return;

        const idx = dlg.messages.findIndex(
          (m) => String(m.id) === String(messageId),
        );
        if (idx === -1) return;

        dlg.messages[idx].is_delivered = true;
      })

      // Seen By Usears ------------------------------------------------------
      .addCase(fetchSeenByUsers.pending, (state) => {
        state.seenByLoading = true;
        state.seenByError = null;
      })
      .addCase(fetchSeenByUsers.fulfilled, (state, action) => {
        const { messageId, users } = action.payload;
        state.seenByUsers[messageId] = users;
        state.seenByLoading = false;
      })
      .addCase(fetchSeenByUsers.rejected, (state, action) => {
        state.seenByLoading = false;
        state.seenByError = action.payload as string;
      })

      // Create Group Chat ---------------------------------------------------
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.loading = false;
        const newGroup: Dialogue = action.payload;
        const exists = state.dialogues.some((d) => d.slug === newGroup.slug);
        if (!exists) {
          state.dialogues.push({ ...newGroup, messages: [] });
        }
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Group Image ---------------------------------------------------
      .addCase(updateGroupImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroupImage.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { dialogueSlug, previewUrl } = action.payload;
        const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (dialogue) {
          dialogue.group_avatar_url = previewUrl;
        }
      })
      .addCase(updateGroupImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Group Info ---------------------------------------------------
      .addCase(updateGroupInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { dialogueSlug, updated } = action.payload as {
          dialogueSlug: string;
          updated: Record<string, any>;
        };

        const applyUpdatedFields = (dlg?: any) => {
          if (!dlg || !updated) return;
          Object.entries(updated).forEach(([field, value]) => {
            (dlg as any)[field] = value;
          });
        };

        const idx = state.dialogues.findIndex(
          (d: any) => d.slug === dialogueSlug,
        );
        if (idx !== -1) {
          applyUpdatedFields(state.dialogues[idx]);
        }
      })

      // Delete Group Chat ---------------------------------------------------
      .addCase(smartDeleteDialogue.fulfilled, (state, action) => {
        state.dialogues = state.dialogues.filter(
          (dialogue) => dialogue.slug !== action.payload.dialogueSlug,
        );
      })

      // Add Participant ------------------------------------------------------
      .addCase(addParticipant.fulfilled, (state, action) => {
        const { dialogueSlug, participantId } = action.payload;
        const participantIdNum = Number(participantId);

        const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (
          dialogue &&
          !dialogue.participants.some((p) => p.id === participantIdNum)
        ) {
          dialogue.participants.push({ id: participantIdNum } as any);
        }
      })

      // Remove Participant ---------------------------------------------------
      .addCase(removeParticipant.fulfilled, (state, action) => {
        const { dialogueSlug, participantId } = action.payload;
        const participantIdNum = Number(participantId);

        const dialogue = state.dialogues.find((d) => d.slug === dialogueSlug);
        if (dialogue) {
          dialogue.participants = dialogue.participants.filter(
            (p) => p.id !== participantIdNum,
          );
        }
      })

      // Fetch Dialogue By Id -------------------------------------------------
      .addCase(fetchDialogueBySlug.fulfilled, (state, action) => {
        const updatedDialogue = action.payload;
        const index = state.dialogues.findIndex(
          (d) => d.slug === updatedDialogue.slug,
        );

        if (index !== -1) {
          const existingDialogue = state.dialogues[index];

          // Preserve locally loaded message state while refreshing dialogue metadata
          state.dialogues[index] = {
            ...existingDialogue,
            ...updatedDialogue,
            messages: existingDialogue.messages || [],
            hasMoreMessages: existingDialogue.hasMoreMessages,
          };
        } else {
          state.dialogues.push({
            ...updatedDialogue,
            messages: [],
          });
        }
      })

      .addCase(fetchGroupParticipants.fulfilled, (state, action) => {
        const { dialogueSlug, participants } = action.payload;
        state.groupParticipants = {
          ...state.groupParticipants,
          [dialogueSlug]: participants,
        };
      })

      .addCase(promoteToElder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(promoteToElder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(promoteToElder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to promote.";
      })

      .addCase(demoteToParticipant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(demoteToParticipant.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(demoteToParticipant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to demote.";
      })

      // Resign Elder Role Group ---------------------------------------
      .addCase(resignElderRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resignElderRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resignElderRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to resign as Elder.";
      })

      // Leave Group -----------------------------------------------------
      .addCase(leaveGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(leaveGroup.fulfilled, (state, action) => {
        const dialogueSlugToRemove = action.payload;
        state.dialogues = state.dialogues.filter(
          (dialogue) => dialogue.slug !== dialogueSlugToRemove,
        );
        state.loading = false;
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to leave group.";
      })

      // Transfer Founder Group ------------------------------------------
      .addCase(transferFounder.pending, (state) => {
        state.loading = true;
      })
      .addCase(transferFounder.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(transferFounder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to transfer founder.";
      })

      // -----------------------------------------------------------------
      .addCase(clearConversationCache, (state) => {
        abortAndClearAllUploads();

        // Preserve authoritative unread badge state
        const preservedUnreadCounts = { ...state.unreadCounts };

        Object.assign(state, initialState);

        // Restore unread counts after cache clear
        state.unreadCounts = preservedUnreadCounts;
      });
  },
});

export const {
  addMessage,
  updateMessage,
  updateEditedMessage,
  updateLastMessage,
  removeTemporaryMessage,
  messageSoftDeleted,
  messageHardDeleted,
  setScrollToMessageId,
  clearScrollToMessageId,
  setHighlightedMessageId,
  clearHighlightedMessageId,
  setTypingStatus,
  updateTypingStatus,
  clearTypingStatus,
  clearAllTypingStatuses,
  setRecording,
  clearRecordingStatus,
  clearAllRecordingStatuses,
  setFileUploadStatus,
  clearFileUploadStatus,
  clearAllFileUploadStatuses,
  removeUploadController,
  setUploadController,
  updateLastSeen,
  setMessageDelivered,
  addNewDialogue,
  removeDialogue,
  removeParticipantLocally,
  updateMyRole,
  incrementUnreadCount,
  resetUnreadCount,
  updateUnreadCount,
  updateDialogueMarker,
  markMediaHydrationDone,
} = conversationSlice.actions;
export default conversationSlice.reducer;
